import { Tooltip } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { LinkedOrganizationDetail } from '~/common/types';
import { Checkbox } from '~/components2/Checkbox';
import Icon from '~/components2/Icon';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import { toPanelId } from './panelIds';

const LINKED_PROJECTS_LABEL = 'Linked Projects';
const LINKED_PROJECTS_PANEL_ID = toPanelId('toc', 'linked-projects');

export default function ProjectsLayerTOC() {
  const { currentOrganizationDetail } = useSnapshot(rootStore) as typeof rootStore;

  if (currentOrganizationDetail.isLoading) return null;
  if (isEmpty(currentOrganizationDetail.linkedOrganizations)) return null;

  return <ProjectsLayerTOCContent linkedOrganizations={currentOrganizationDetail.linkedOrganizations} />;
}

function ProjectsLayerTOCContent({ linkedOrganizations }: { linkedOrganizations?: LinkedOrganizationDetail[] }) {
  const { visibleLinkedOrganizationIds, linkedOrgsSectionExpanded } = useSnapshot(layerStore) as typeof layerStore;

  const linkedOrgsTOC = useMemo(() => {
    const allChecked = linkedOrganizations?.every((org) => visibleLinkedOrganizationIds.has(org.id));

    return (
      <div className="my-2 px-1">
        <div className="group flex cursor-pointer items-center justify-between space-x-2">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              data-testid="toc-section-expand-linked-projects"
              className="inline-flex appearance-none border-0 bg-transparent p-0"
              onClick={() => {
                layerStore.toggleLinkedOrgSection();
              }}
              aria-label={LINKED_PROJECTS_LABEL}
              aria-expanded={linkedOrgsSectionExpanded}
              aria-controls={linkedOrgsSectionExpanded ? LINKED_PROJECTS_PANEL_ID : undefined}
            >
              <Icon icon={linkedOrgsSectionExpanded ? 'chevronDown' : 'chevronRight'} />
            </button>

            <Tooltip asChild text={LINKED_PROJECTS_LABEL}>
              <span>
                <Checkbox
                  checked={allChecked}
                  isDisabled={false}
                  onChange={(value) => {
                    if (value === true) {
                      linkedOrganizations?.forEach((org) => {
                        layerStore.visibleLinkedOrganizationIds.add(org.id);
                      });
                    } else {
                      layerStore.clearVisibleLinkedOrganizationIds();
                    }
                  }}
                  text={LINKED_PROJECTS_LABEL}
                />
              </span>
            </Tooltip>
          </div>
        </div>

        {linkedOrgsSectionExpanded && (
          <div id={LINKED_PROJECTS_PANEL_ID} className="my-2 ml-2.5">
            {linkedOrganizations?.map((detail) => {
              return (
                <div
                  key={`org-projects-${detail.id}`}
                  className="group relative my-1 ml-7 flex min-h-[1.75rem] flex-row items-center justify-between space-x-2"
                >
                  <div className="grow">
                    <Tooltip asChild text={detail.name}>
                      <span>
                        <Checkbox
                          checked={visibleLinkedOrganizationIds.has(detail.id)}
                          onChange={(value) => {
                            if (value === true) {
                              layerStore.visibleLinkedOrganizationIds.add(detail.id);
                            } else {
                              layerStore.visibleLinkedOrganizationIds.delete(detail.id);
                            }
                          }}
                          text={detail.name}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [visibleLinkedOrganizationIds, linkedOrganizations, linkedOrgsSectionExpanded]);

  return <>{linkedOrgsTOC}</>;
}
